/**
 * Builds the complete export file tree from a project.
 *
 * Every full id, lang-key root and texture short-name is derived through the
 * helpers in `model/identifiers.ts`. Nothing composed is read back out of the
 * project document (Section 9.3).
 */

import {
  fullId,
  guideCategoryLangKey,
  guideComponentId,
  guideEntryLangKey,
  guideHomeTitleLangKey,
  guideIntroLangKey,
  itemNameLangKey,
  resolveRef,
  textureShortName,
} from "../model/identifiers";
import type { Item, ItemRef, Project, Recipe } from "../model/types";
import { buildBehaviorManifest, buildResourceManifest } from "./manifest";
import { buildMainJs } from "./mainjs";
import { dataUrlToBytes, placeholderPng } from "./assets";
import { mergeLang, renderLangEntries, type LangEntry } from "./langMerge";

export interface ExportFile {
  path: string;
  content: string | Uint8Array;
}

const FORMAT_VERSION = "1.21.0";
// Custom Components v2 needs a recent item format version.
const ITEM_FORMAT_VERSION = "1.21.70";

function json(value: unknown): string {
  return JSON.stringify(value, null, 2) + "\n";
}

// --- Item definitions ------------------------------------------------------

function buildItemJson(project: Project, id: string, stackSize: number, isGuide = false): unknown {
  const shortName = textureShortName(project.namespace, id);
  const components: Record<string, unknown> = {
    // Modern data-driven item icon uses the short-name string form.
    "minecraft:icon": { texture: shortName },
    "minecraft:max_stack_size": stackSize,
  };
  // The guide item carries the custom component that opens the guide on use.
  if (isGuide) {
    components["minecraft:custom_components"] = [guideComponentId(project.namespace)];
  }
  return {
    format_version: ITEM_FORMAT_VERSION,
    "minecraft:item": {
      description: {
        identifier: fullId(project.namespace, id),
        menu_category: { category: "items" },
      },
      components,
    },
  };
}

// --- Recipe definitions ----------------------------------------------------

/** One ingredient / key slot: a single item, or "any item with this tag". */
type RecipeSlot = { item: string } | { tag: string };

function slotJson(namespace: string, ref: ItemRef): RecipeSlot {
  const id = resolveRef(namespace, ref);
  return ref.tag ? { tag: id } : { item: id };
}

function buildRecipeJson(project: Project, recipe: Recipe): unknown {
  const identifier = fullId(project.namespace, recipe.id);
  // A result is always a concrete item: the game cannot craft "a tag".
  const result = { item: resolveRef(project.namespace, recipe.result), count: recipe.resultCount };

  if (recipe.shape === "shapeless") {
    return {
      format_version: FORMAT_VERSION,
      "minecraft:recipe_shapeless": {
        description: { identifier },
        tags: recipe.tags,
        ingredients: recipe.ingredients.map((ref) => slotJson(project.namespace, ref)),
        result,
      },
    };
  }

  const key: Record<string, RecipeSlot> = {};
  for (const [k, ref] of Object.entries(recipe.key)) {
    key[k] = slotJson(project.namespace, ref);
  }
  return {
    format_version: FORMAT_VERSION,
    "minecraft:recipe_shaped": {
      description: { identifier },
      tags: recipe.tags,
      pattern: recipe.pattern,
      key,
      result,
    },
  };
}

// --- Textures --------------------------------------------------------------

function buildItemTextureJson(project: Project): unknown {
  const texture_data: Record<string, { textures: string }> = {};
  const ids = [project.guideItemId, ...project.items.map((i) => i.id)];
  for (const id of ids) {
    texture_data[textureShortName(project.namespace, id)] = { textures: `textures/items/${id}` };
  }
  return {
    resource_pack_name: project.namespace,
    texture_name: "atlas.items",
    texture_data,
  };
}

// --- Lang files ------------------------------------------------------------

/**
 * The lang keys this tool manages, for one locale. Values are raw (unescaped);
 * the renderer/merger escapes newlines. Used both to render a fresh lang file
 * and to merge into an imported base (see `langMerge.ts`).
 */
export function buildLangEntries(project: Project, code: string): LangEntry[] {
  // Resolve a per-language value, falling back to en_US.
  const tr = (map: Record<string, string>) => map[code] ?? map.en_US ?? "";
  // A value is "present" when some language has non-whitespace content.
  const present = (map: Record<string, string>) => Object.values(map).some((v) => (v ?? "").trim() !== "");
  const entries: LangEntry[] = [];

  // A label key (title/name) must never be empty, or Bedrock shows the raw key.
  const pushLabel = (key: string, map: Record<string, string>, fallback: string) => {
    const v = tr(map).trim();
    entries.push({ key, value: v !== "" ? tr(map) : fallback });
  };
  // A body key is only emitted when it has content; a blank optional field
  // therefore never produces an empty value that renders as a raw key in-game.
  const pushBody = (key: string, map: Record<string, string>) => {
    if (present(map)) entries.push({ key, value: tr(map) });
  };

  entries.push({ key: "pack.name", value: project.meta.name || project.namespace });
  entries.push({ key: "pack.description", value: project.meta.description });

  // Guide item display name + homepage title/intro.
  pushLabel(itemNameLangKey(project.namespace, project.guideItemId), project.guide.title, project.guideItemId);
  pushLabel(guideHomeTitleLangKey(project.namespace), project.guide.title, project.guideItemId);
  pushBody(guideIntroLangKey(project.namespace), project.guide.intro);

  // Custom item display names.
  for (const item of project.items) {
    pushLabel(itemNameLangKey(project.namespace, item.id), item.displayName, item.id);
  }

  // Guide categories + entries.
  for (const cat of project.guide.categories) {
    pushLabel(guideCategoryLangKey(project.namespace, cat.id, "title"), cat.title, cat.id);
    pushBody(guideCategoryLangKey(project.namespace, cat.id, "body"), cat.body);
    for (const entry of cat.entries) {
      pushLabel(guideEntryLangKey(project.namespace, cat.id, entry.id, "title"), entry.title, entry.id);
      pushBody(guideEntryLangKey(project.namespace, cat.id, entry.id, "body"), entry.body);
    }
  }

  return entries;
}

/**
 * Produce the lang-file text for a locale. If the project was imported and has
 * a base lang file for this locale, MERGE managed keys into it (preserving all
 * other translations); otherwise render a fresh file.
 */
function buildLangFile(project: Project, code: string): string {
  const entries = buildLangEntries(project, code);
  const base = project.importedLang?.[code];
  return base !== undefined ? mergeLang(base, entries) : renderLangEntries(entries);
}

// --- Full tree -------------------------------------------------------------

export function buildExportFiles(project: Project): ExportFile[] {
  const bp = `${project.namespace}_BP`;
  const rp = `${project.namespace}_RP`;
  const files: ExportFile[] = [];

  // Behavior pack
  files.push({ path: `${bp}/manifest.json`, content: json(buildBehaviorManifest(project)) });
  files.push({ path: `${bp}/pack_icon.png`, content: placeholderPng() });
  files.push({ path: `${bp}/scripts/main.js`, content: buildMainJs(project) });

  // Guide item (stack size 1, carries the custom component) + custom items.
  files.push({ path: `${bp}/items/${project.guideItemId}.json`, content: json(buildItemJson(project, project.guideItemId, 1, true)) });
  for (const item of project.items) {
    files.push({ path: `${bp}/items/${item.id}.json`, content: json(buildItemJson(project, item.id, 64)) });
  }

  // Recipes
  for (const recipe of project.recipes) {
    files.push({ path: `${bp}/recipes/${recipe.id}.json`, content: json(buildRecipeJson(project, recipe)) });
  }

  // Resource pack
  files.push({ path: `${rp}/manifest.json`, content: json(buildResourceManifest(project)) });
  files.push({ path: `${rp}/pack_icon.png`, content: placeholderPng() });
  files.push({ path: `${rp}/textures/item_texture.json`, content: json(buildItemTextureJson(project)) });

  // Item texture PNGs (guide + custom items).
  files.push({ path: `${rp}/textures/items/${project.guideItemId}.png`, content: placeholderPng() });
  for (const item of project.items) {
    const bytes = item.textureDataUrl ? dataUrlToBytes(item.textureDataUrl) : placeholderPng();
    files.push({ path: `${rp}/textures/items/${item.id}.png`, content: bytes });
  }

  // Lang files
  files.push({ path: `${rp}/texts/languages.json`, content: json(project.languages.map((l) => l.code)) });
  for (const lang of project.languages) {
    files.push({ path: `${rp}/texts/${lang.code}.lang`, content: buildLangFile(project, lang.code) });
  }

  return files;
}

export const _internal = { buildItemJson, buildRecipeJson, buildLangFile, buildItemTextureJson };
export type { Item };
